import React, { FC, useCallback, useEffect, useRef } from 'react';
import { NavBar, NavBarProps } from '../../../NavBar';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { Category, EventPageFragment, EventPageType, EventType } from '@graphql/generated';
import { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { VerticalAlignment, WebsiteHeaderColors, WebsiteHeaderTextContent } from '@apps/guest/websiteHeader';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { InlineEditor } from '@shared/components';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { withWindow } from '@shared/utils/withWindow';
import { AnimatedBorder } from '@shared/components/InlineEditor/components/AnimatedBorder/AnimatedBorder';
import { useFitText } from '../../hooks/useFitText';
import { useEventInfo } from '@shared/utils/eventInfo';

export interface EventNameNavForStandaloneHeaderProps {
  navBarVariant: NavBarProps['variant'];
  eventHandle: string;
  pages: ReadonlyArray<EventPageFragment>;
  handleMenuClicked?: (item: string) => void;
  eventProps: NavbarEventPropsInterface;
  currentPageType: Maybe<EventPageType>;
  pageSlug?: string;
}

interface Props extends EventNameNavForStandaloneHeaderProps {
  colors?: WebsiteHeaderColors | undefined;
  headerTextData?: WebsiteHeaderTextContent | undefined;
  eventDisplayName: Maybe<string>;
  isCompactView: boolean;
  handleEditTextClick: () => void;
  isContentEditable: boolean;
  editableContentRef?: React.RefObject<HTMLDivElement>;
  forceRenderViewIn?: 'mobile' | 'desktop';
  scaleValue?: number;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
}

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

const computeResponsiveValue = <T,>(defaultValue: T, mobileValue: number, desktopValue: number, forceRenderViewIn?: 'mobile' | 'desktop') => {
  if (forceRenderViewIn === 'mobile') {
    return mobileValue;
  }
  if (forceRenderViewIn === 'desktop') {
    return desktopValue;
  }
  return defaultValue;
};

const TEXT_MAX_CHAR_LIMIT = 70;
const CONTAINER_TOTAL_MARGIN = 80;
const INPUT_AREA__TOTAL_PADDING_Y = 64;
const INPUT_AREA__TOTAL_PADDING_X = 48;
const INLINE_WRAPPER_MIN_WIDTH = 180;

const getVerticalPlacement = (alignment: VerticalAlignment, forceRenderViewIn?: 'mobile' | 'desktop') => {
  switch (alignment) {
    case 'top':
      return { top: computeResponsiveValue({ _: 24, sm4: 0, lg: 0 }, 24, 0, forceRenderViewIn) };
    case 'center':
      return {
        top: '50%',
        transform: 'translateY(-50%)',
      };
    case 'bottom':
      return { bottom: computeResponsiveValue({ _: 24, md: 64, lg: 64 }, 24, 64, forceRenderViewIn) };
  }
};

export const EventNameNavForStandaloneHeader: FC<Props> = props => {
  const {
    handleMenuClicked,
    navBarVariant,
    eventHandle,
    eventProps,
    pages,
    eventDisplayName,
    colors,
    headerTextData,
    isCompactView,
    currentPageType,
    pageSlug,
    handleEditTextClick,
    isContentEditable = false,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    renderedContainerHeight,
  } = props;

  const navBarRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  // editableContentRef is not always available, so in that case we need to manage the ref ourselves
  const textRefToPass = editableContentRef || textRef;

  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isXsScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'xs' }));

  const [fontSize] = useResponsive({ values: { _: 64, md: 100, lg: 125 } }, 64);

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const [inputAreaMarginX] = useResponsive({ values: { _: 24, md: 64, lg: 64 } }, 64);

  // widthOffset is input area total outer margin + input area total padding
  const widthOffset = computeResponsiveValue(inputAreaMarginX, 24, 64, forceRenderViewIn) * 2 + INPUT_AREA__TOTAL_PADDING_X;

  const { containerRef, fontSize: fitFontSize } = useFitText({
    textRef: textRefToPass,
    maxFontSize: Number.parseFloat(scaleFontSize(pxToRem(computeResponsiveValue(fontSize, 64, 125, forceRenderViewIn)), Category.EVENT_TITLE)) * 16,
    widthOffset,
    heightOffset: INPUT_AREA__TOTAL_PADDING_Y,
    minFontSize: 10,
  });

  const { eventInfo } = useEventInfo();

  const { tq } = useTranslation('joykit');

  useEffect(() => {
    if (editableContentRef?.current && isContentEditable) {
      editableContentRef.current.focus();
      withWindow(globalWindow => {
        // place caret at end of the text
        const range = globalWindow.document.createRange();
        range.selectNodeContents(editableContentRef.current as Node);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  }, [editableContentRef, isContentEditable]);

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  const handleInput = useEventCallback(() => {
    if (!editableContentRef?.current) return;

    const element = editableContentRef.current;
    const textContent = element.innerText;
    if (textContent.length > TEXT_MAX_CHAR_LIMIT) {
      element.innerText = textContent.slice(0, TEXT_MAX_CHAR_LIMIT);
      // Restore caret to end
      withWindow(globalWindow => {
        const range = globalWindow.document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  });

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  // if hasModifiedEventNameOnce is true then show headerTextData.content. In case of hasModifiedEventNameOnce is false or undefined we need to show the eventDisplayName
  const textContent = (headerTextData?.hasModifiedEventNameOnce ? headerTextData.content : eventDisplayName) || '';

  return (
    <>
      <Box
        ref={navBarRef}
        width="100%"
        pointerEvents={isContentEditable ? 'none' : 'unset'}
        position="relative"
        zIndex="docked"
      >
        <NavBar
          handleMenuClicked={handleMenuClicked}
          title={eventDisplayName}
          variant={navBarVariant}
          data-testid={'header-nav'}
          eventHandle={eventHandle}
          pages={pages}
          eventProps={eventProps}
          textColor={colors?.nameAndNavFontColor}
          backgroundColor={colors?.nameAndNavBackgroundColorEnabled ? colors?.nameAndNavBackgroundColor : undefined}
          paddingBottom={isCompactView ? 8 : 0}
        />
      </Box>
      {!isCompactView && (
        <Flex
          ref={containerRef}
          position="relative"
          width="100%"
          height={`calc(${renderedContainerHeight ? `${renderedContainerHeight}px` : '100dvh'} - ${navBarRef.current?.clientHeight || 0}px - ${CONTAINER_TOTAL_MARGIN}px)`}
          marginTop={8}
        >
          <Flex
            width="100%"
            maxHeight="100%"
            position="absolute"
            zIndex="docked"
            {...getVerticalPlacement(headerTextData?.pageAlignment?.y || 'center', forceRenderViewIn)}
            justifyContent={headerTextData?.pageAlignment?.x || headerTextData?.textAlignment || 'left'}
            paddingX={computeResponsiveValue({ _: 24, md: 64, lg: 64 }, 24, 64, forceRenderViewIn)}
          >
            <Box
              maxWidth="100%"
              paddingY={INPUT_AREA__TOTAL_PADDING_Y / 2}
              paddingX={6}
              backgroundColor={colors?.nameAndNavBackgroundColorEnabled ? colors?.nameAndNavBackgroundColor : undefined}
            >
              <InlineEditor
                elementLabel={eventInfo?.eventType === EventType.wedding ? tq`Couple's Name` : tq`Event Display Name`}
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                  minWidth: textContent.trim() ? 'unset' : pxToRem(INLINE_WRAPPER_MIN_WIDTH),
                }}
                inset={isMobile ? { x: -20, y: -24 } : { x: -16, y: -20 }}
                componentName="couplesName"
                pageName={currentPageType}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={typographyOverride}>
                  {isContentEditable ? (
                    <Box position="relative">
                      <AnimatedBorder
                        isVisibile={true}
                        borderWidth={pxToRem(applyScaling(2))}
                        borderRadius={`${applyScaling(8)}px`}
                        inset={isMobile ? { x: '-20px', y: '-24px' } : { x: '-16px', y: '-20px' }}
                        padding={0}
                      />
                      <Box
                        ref={textRefToPass}
                        position="relative"
                        typographyVariant={isXsScreen ? 'display4' : 'display6'}
                        color={colors?.nameAndNavFontColor || 'mono14'}
                        zIndex={11}
                        onInput={handleInput}
                        contentEditable={isContentEditable}
                        dangerouslySetInnerHTML={{ __html: textContent }}
                        textAlign={headerTextData?.textAlignment || 'left'}
                        whiteSpace="pre-wrap"
                        outline="none"
                        maxHeight={`calc(${renderedContainerHeight ? `${renderedContainerHeight}px` : '100dvh'} - ${
                          navBarRef.current?.clientHeight || 0
                        }px - ${CONTAINER_TOTAL_MARGIN}px - ${INPUT_AREA__TOTAL_PADDING_Y}px)`}
                        overflow="hidden"
                        top="0"
                        style={{
                          caretColor: '#4E06F2',
                          fontSize: fitFontSize,
                        }}
                      />
                    </Box>
                  ) : (
                    <TextV2
                      ref={textRefToPass}
                      as="div"
                      typographyVariant={isXsScreen ? 'display4' : 'display6'}
                      color={colors?.nameAndNavFontColor || 'mono14'}
                      whiteSpace="pre-wrap"
                      textAlign={headerTextData?.textAlignment || 'left'}
                      position="relative"
                      style={{ fontSize: fitFontSize }}
                    >
                      {textContent}
                    </TextV2>
                  )}
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </Box>
          </Flex>
        </Flex>
      )}
    </>
  );
};

EventNameNavForStandaloneHeader.displayName = 'EventNameNavForStandaloneHeader';
